import { useState } from "react";
import { useAuth } from '../../utility/auth';
import CryptoJS from "crypto-js";


const FLASK_URL = process.env.REACT_APP_FLASK_URL 
const ENCRYPTION_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

export const encryptChatData = (data) => {
  if (!ENCRYPTION_KEY) {
    throw new Error("REACT_APP_ENCRYPTION_KEY is not configured");
  }
  return CryptoJS.AES.encrypt(JSON.stringify(data), ENCRYPTION_KEY).toString();
};

export const decryptChatData = (encryptedData) => {
  if (!ENCRYPTION_KEY) {
    throw new Error("REACT_APP_ENCRYPTION_KEY is not configured");
  }
  const decryptedText = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY).toString(CryptoJS.enc.Utf8);
  if (!decryptedText) {
    throw new Error("Unable to decrypt chatbot response");
  }
  return JSON.parse(decryptedText);
};

const getEncryptedResponse = (responseData) => {
  if (typeof responseData === "string") return responseData;
  return responseData?.data || responseData?.encryptedData || responseData?.body;
};

// ── Shared table styles (small, fits the 340px-wide chat window with
//    horizontal scroll rather than squishing columns unreadably) ──────────
const tableWrapperStyle = { marginTop: 8, overflowX: "auto" };
const tableStyle = { borderCollapse: "collapse", minWidth: "480px", width: "100%", fontSize: "11px" };
const thStyle = { border: "1px solid #d0d7e6", padding: "4px 8px", textAlign: "left", background: "#e8eefc", fontWeight: 600, whiteSpace: "nowrap" };
const tdStyle = { border: "1px solid #e5e5e5", padding: "4px 8px", whiteSpace: "nowrap" };
const captionStyle = { fontSize: "11px", color: "#666", marginBottom: 4, fontWeight: "900" };
const categoryHeadingStyle = { fontWeight: 600, fontSize: "12px", marginBottom: 4, marginTop: 8 };

// Splits text on URLs and renders them as real, safe <a> elements — no
// dangerouslySetInnerHTML needed, so nothing the model/backend returns can
// inject arbitrary HTML/scripts. Everything else stays as plain escaped text.
// Uses a fresh RegExp per test (not a single reused global regex object) so
// there's no reliance on lastIndex state between calls.
const URL_SPLIT_PATTERN = /(https?:\/\/[^\s]+)/g;
const isUrl = (part) => /^https?:\/\/[^\s]+$/.test(part);

function renderTextWithLinks(text) {
  if (!text) return null;
  const parts = text.split(URL_SPLIT_PATTERN);
  return parts.map((part, i) =>
    isUrl(part) ? (
      <a key={i} href={part} target="_blank" rel="noopener noreferrer" style={{ color: "inherit", textDecoration: "underline" }}>
        {part}
      </a>
    ) : (
      <span key={i}>{part}</span>
    )
  );
}

function RecordsTable({ records, totalCount }) {
  if (!records || records.length === 0) return null;
  return (
    <div style={tableWrapperStyle}>
      {typeof totalCount === "number" && (
        <div style={captionStyle}>
          Total: {totalCount}
          {totalCount > records.length ? ` — showing ${records.length} most recent` : ""}
        </div>
      )}
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>#</th>
            <th style={thStyle}>Name</th>
            <th style={thStyle}>Status</th>
            {/* <th style={thStyle}>Email</th> */}
            <th style={thStyle}>PAN</th>
            {/* <th style={thStyle}>Mobile</th> */}
            <th style={thStyle}>Service Name</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r, idx) => (
            <tr key={idx}>
              <td style={tdStyle}>{idx + 1}</td>
              <td style={tdStyle}>{r.name || "N/A"}</td>
              <td style={tdStyle}>{r.status || "N/A"}</td>
              {/* <td style={tdStyle}>{r.email || "N/A"}</td> */}
              <td style={tdStyle}>{r.pan || "N/A"}</td>
              {/* <td style={tdStyle}>{r.mobile || "N/A"}</td> */}
              <td style={tdStyle}>{r.serviceName || "N/A"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CategoriesBreakdown({ categories }) {
  if (!categories) return null;
  return (
    <div>
      {Object.entries(categories).map(([label, value]) => (
        <div key={label}>
          <div style={categoryHeadingStyle}>
            {label}: {value?.total_count ?? 0} pending client{value?.total_count === 1 ? "" : "s"}
          </div>
          {value?.config_error && (
            <div style={{ fontSize: "11px", color: "#c0392b" }}>{value.config_error}</div>
          )}
          <RecordsTable records={value?.records} totalCount={value?.total_count} />
        </div>
      ))}
    </div>
  );
}

export default function Chatbot() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { from: "ai", text: "Welcome! I am your EKYC AI Assistant. Ask me anything related to EKYC." }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const auth = useAuth();
  const user = auth.decoder(sessionStorage.getItem('user'));
  const { data } = user;
  const { tenantId } = data?.UserInfo;

  const sendMessage = async () => {
    if (!input.trim()) return;
    const userMsg = input.trim();
    setMessages(prev => [...prev, { from: "user", text: userMsg }]);
    setInput("");
    setLoading(true);

    try {
      const res = await fetch(`${FLASK_URL}/chat`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Tenant-ID": tenantId
        },
        body: JSON.stringify({ data: encryptChatData({ message: userMsg }) })
      });
      if (!res.ok) {
        throw new Error(`Chatbot request failed with status ${res.status}`);
      }
      const encryptedResponse = getEncryptedResponse(await res.json());
      const resData = decryptChatData(encryptedResponse);
      // console.log('resData :', resData);
      // Keep the structured pieces (records / categories / total_count)
      // alongside the text, so they can be rendered as real tables below
      // instead of being lost.
      setMessages(prev => [...prev, {
        from: "ai",
        text: resData.response,
        records: resData.records,
        totalCount: resData.total_count,
        categories: resData.categories,
      }]);
    } catch {
      setMessages(prev => [...prev, { from: "ai", text: "Service unavailable. Please try again." }]);
    }
    setLoading(false);
  };

  return (
    <>
      {/* Floating Button */}
      <button
        onClick={() => setOpen(!open)}
        style={{
          position: "fixed", bottom: "24px", right: "24px",
          width: "56px", height: "56px", borderRadius: "50%",
          background: "#1565c0", color: "white", border: "none",
          fontSize: "24px", cursor: "pointer", zIndex: 9999,
          boxShadow: "0 4px 16px rgba(0,0,0,0.2)"
        }}
      >
        {open ? "✕" : "🤖"}
      </button>

      {/* Chat Window */}
      {open && (
        <div style={{
          position: "fixed", bottom: "90px", right: "24px",
          width: "500px", height: "480px", background: "#d0d1d1",
          // background: #a1a4a4
          borderRadius: "16px", boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
          display: "flex", flexDirection: "column", zIndex: 9999
        }}>
          {/* Header */}
          <div style={{
            background: "#1565c0", color: "white", padding: "14px 16px",
            borderRadius: "16px 16px 0 0", fontWeight: "600"
          }}>
            🤖 EKYC AI Assistant
            <div style={{ fontSize: "11px", opacity: 0.8 }}>Ask anything related to EKYC</div>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: "auto", padding: "12px",
            display: "flex", flexDirection: "column", gap: "8px"
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                alignSelf: msg.from === "user" ? "flex-end" : "flex-start",
                background: msg.from === "user" ? "#1565c0" : "#f0f4ff",
                color: msg.from === "user" ? "white" : "#333",
                padding: "8px 12px", borderRadius: "12px",
                maxWidth: msg.records?.length || msg.categories ? "100%" : "80%",
                fontSize: "13px", lineHeight: "1.5",
                whiteSpace: "pre-wrap", wordBreak: "break-word"
              }}>
                {renderTextWithLinks(msg.text)}
                {msg.records && <RecordsTable records={msg.records} totalCount={msg.totalCount} />}
                {msg.categories && <CategoriesBreakdown categories={msg.categories} />}
              </div>
            ))}

            {loading && (
              <div
                style={{
                  alignSelf: "flex-start", background: "#f0f4ff",
                  padding: "8px 12px", borderRadius: "12px", fontSize: "13px",
                  display: "flex",
                  gap: "4px",
                  alignItems: "center",
                }}
              >
                {[0, 1, 2].map((i) => (
                  <span
                    key={i}
                    style={{
                      width: "6px",
                      height: "6px",
                      background: "#6b7280",
                      borderRadius: "50%",
                      display: "inline-block",
                      animation: "typing 1.4s infinite ease-in-out",
                      animationDelay: `${i * 0.2}s`,
                    }}
                  />
                ))}

                <style>
                  {`
                     @keyframes typing {
                       0%, 60%, 100% {
                         transform: translateY(0);
                         opacity: 0.4;
                       }
                       30% {
                         transform: translateY(-5px);
                         opacity: 1;
                       }
                     }
                   `}
                </style>
              </div>
            )}
          </div>

          {/* Input */}
          <div style={{
            padding: "10px", borderTop: "1px solid #eee",
            display: "flex", gap: "8px"
          }}>
            <input
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyPress={e => e.key === "Enter" && sendMessage()}
              placeholder="Ask anything..."
              style={{
                flex: 1, border: "1px solid #ddd", borderRadius: "8px",
                padding: "8px 12px", fontSize: "13px", outline: "none"
              }}
            />
            <button
              onClick={sendMessage}
              style={{
                background: "#1565c0", color: "white", border: "none",
                borderRadius: "8px", padding: "8px 14px", cursor: "pointer"
              }}
            >
              ➤
            </button>
          </div>
        </div>
      )}
    </>
  );
}